import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Switch } from 'antd';
import { formatNumber } from '@/utils/formatNumber';
import { Sun, Activity } from 'lucide-react';

// Add type declaration for window.echarts
declare global {
  interface Window {
    echarts: any;
  }
}

export interface EnergyData {
  datetime: string;
  solar: number;
  grid: number;
  indoorTemp: number;
  outdoorTemp: number;
  ahuWorkingPercent: number;
  operation?: string;
}

interface SolarStackBarProps {
  data: EnergyData[];
}

export const SolarStackBar: React.FC<SolarStackBarProps> = ({ data }) => {
  const [showSecondaryLines, setShowSecondaryLines] = useState(false);
  const [showOperationView, setShowOperationView] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);

  // Calculate operation statistics
  const operationStats = useMemo(() => {
    const cebcoData = data.filter(d => d.operation === 'CEB-CO');
    const noCebcoData = data.filter(d => d.operation !== 'CEB-CO');
    
    const cebcoStats = {
      days: cebcoData.length,
      totalGrid: cebcoData.reduce((sum, d) => sum + d.grid, 0),
      totalSolar: cebcoData.reduce((sum, d) => sum + d.solar, 0),
      avgGrid: cebcoData.length > 0 ? cebcoData.reduce((sum, d) => sum + d.grid, 0) / cebcoData.length : 0,
      avgSolar: cebcoData.length > 0 ? cebcoData.reduce((sum, d) => sum + d.solar, 0) / cebcoData.length : 0,
    };
    
    const noCebcoStats = {
      days: noCebcoData.length,
      totalGrid: noCebcoData.reduce((sum, d) => sum + d.grid, 0),
      totalSolar: noCebcoData.reduce((sum, d) => sum + d.solar, 0),
      avgGrid: noCebcoData.length > 0 ? noCebcoData.reduce((sum, d) => sum + d.grid, 0) / noCebcoData.length : 0,
      avgSolar: noCebcoData.length > 0 ? noCebcoData.reduce((sum, d) => sum + d.solar, 0) / noCebcoData.length : 0,
    };
    
    // Calculate solar percentages
    cebcoStats.solarPercent = (cebcoStats.totalGrid + cebcoStats.totalSolar) > 0 
      ? (cebcoStats.totalSolar / (cebcoStats.totalGrid + cebcoStats.totalSolar)) * 100 
      : 0;
    noCebcoStats.solarPercent = (noCebcoStats.totalGrid + noCebcoStats.totalSolar) > 0 
      ? (noCebcoStats.totalSolar / (noCebcoStats.totalGrid + noCebcoStats.totalSolar)) * 100 
      : 0;
    
    return { cebco: cebcoStats, noCebco: noCebcoStats };
  }, [data]);

  const initializeChart = () => {
    if (chartRef.current && window.echarts) {
      chartInstance.current = window.echarts.init(chartRef.current);
      updateChart();
    }
  };

  const updateChart = () => {
    if (!chartInstance.current || !data.length) return;

    const dates = data.map(item => item.datetime);
    const solarData = data.map(item => item.solar);
    const gridData = data.map(item => item.grid);
    const indoorTempData = data.map(item => item.indoorTemp);
    const outdoorTempData = data.map(item => item.outdoorTemp);

    // Prepare series based on operation view
    const series: any[] = [];
    
    if (showOperationView) {
      // Operation view: show different colors for CEB-CO vs No CEB-CO
      const gridDataWithColors = data.map(item => ({
        value: item.grid,
        itemStyle: {
          color: item.operation === 'CEB-CO' ? '#85348C' : '#4B5563' // Purple for CEB-CO, dark gray for No CEB-CO
        }
      }));
      
      const solarDataWithColors = data.map(item => ({
        value: item.solar,
        itemStyle: {
          color: item.operation === 'CEB-CO' ? '#22c55e' : '#9CA3AF' // Green for CEB-CO, gray for No CEB-CO
        }
      }));
      
      series.push(
        {
          name: 'Grid',
          type: 'bar',
          stack: 'energy',
          data: gridDataWithColors,
          barWidth: '50%',
          barGap: '30%',
          barCategoryGap: '40%'
        },
        {
          name: 'Solar',
          type: 'bar',
          stack: 'energy',
          data: solarDataWithColors,
          barWidth: '50%'
        }
      );
    } else {
      // Normal view: standard colors
      series.push(
        {
          name: 'Grid',
          type: 'bar',
          stack: 'energy',
          data: gridData,
          itemStyle: {
            color: '#85348C'
          },
          barWidth: '50%',
          barGap: '30%',
          barCategoryGap: '40%',
          emphasis: {
            itemStyle: {
              color: '#6b2870'
            }
          }
        },
        {
          name: 'Solar',
          type: 'bar',
          stack: 'energy',
          data: solarData,
          itemStyle: {
            color: '#22c55e'
          },
          barWidth: '50%',
          emphasis: {
            itemStyle: {
              color: '#16a34a'
            }
          }
        }
      );
    }

    if (showSecondaryLines) {
      series.push(
        {
          name: 'Indoor Temp',
          type: 'line',
          yAxisIndex: 1,
          data: indoorTempData,
          lineStyle: {
            color: '#0ea5e9',
            width: 2
          },
          itemStyle: {
            color: '#0ea5e9'
          },
          symbol: 'none'
        },
        {
          name: 'Outdoor Temp',
          type: 'line',
          yAxisIndex: 1,
          data: outdoorTempData,
          lineStyle: {
            color: '#f97316',  // orange color
            width: 2
          },
          itemStyle: {
            color: '#f97316'  // orange color for legend icon
          },
          symbol: 'none'
        }
      );
    }

    const option = {
      grid: {
        left: '3%',
        right: showSecondaryLines ? '5%' : '3%',
        bottom: '2%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          rotate: 45,
          fontSize: 8
        }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Energy (kWh)',
          nameLocation: 'middle',
          nameGap: 50,
          axisLabel: {
            fontSize: 11
          }
        },
        ...(showSecondaryLines ? [{
          type: 'value',
          name: 'Temperature (°C)',
          nameLocation: 'middle',
          nameGap: 50,
          position: 'right',
          axisLabel: {
            fontSize: 11
          }
        }] : [])
      ],
      legend: {
        data: showSecondaryLines ? [
          'Grid', 
          'Solar', 
          'Indoor Temp', 
          {
            name: 'Outdoor Temp',
            textStyle: {
              color: '#f97316'  // orange color
            }
          }
        ] : ['Grid', 'Solar'],
        top: 0,
        textStyle: {
          fontSize: 12
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          let html = `<div style="padding: 10px;"><strong>${params[0].name}</strong><br/>`;
          params.forEach((param: any) => {
            let unit = ' kWh';
            if (param.seriesName.includes('Temp')) {
              unit = '°C';
            }
            html += `<span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 5px; border-radius: 2px;"></span>`;
            html += `${param.seriesName}: ${formatNumber(param.value, 1)}${unit}<br/>`;
          });
          html += '</div>';
          return html;
        }
      },
      series: series
    };

    // Clear previous option and set new one
    chartInstance.current.clear();
    chartInstance.current.setOption(option);

    // Handle legend toggle with native ECharts functionality
    chartInstance.current.off('legendselectchanged');
    chartInstance.current.on('legendselectchanged', function (params: any) {
      // ECharts handles the show/hide automatically
    });
  };

  // Load ECharts
  useEffect(() => {
    if (!window.echarts) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js';
      script.async = true;
      script.onload = initializeChart;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    } else {
      initializeChart();
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, []);

  // Update chart when data or settings change
  useEffect(() => {
    if (chartInstance.current) {
      updateChart();
    }
  }, [data, showSecondaryLines, showOperationView]);

  // Resize chart when operation view changes
  useEffect(() => {
    if (chartInstance.current) {
      // Add a small delay to ensure DOM has updated
      setTimeout(() => {
        chartInstance.current.resize();
      }, 100);
    }
  }, [showOperationView]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-grey p-4 h-full flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-3 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
            <Sun className="h-3 w-3 text-primary" />
          </div>
          <h3 className="text-dark-blue heading-7">Grid Consumption & Solar</h3>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <Switch
              checked={showOperationView}
              onChange={setShowOperationView}
              size="small"
            />
            <span className="paragraph-12 text-dark-grey">Show operation mode</span>
          </label>
          <label className="flex items-center gap-2">
            <Switch
              checked={showSecondaryLines}
              onChange={setShowSecondaryLines}
              size="small"
            />
            <span className="paragraph-12 text-dark-grey">Show temperature lines</span>
          </label>
        </div>
      </div>
      
      {showOperationView && (
        <div className="mb-2 p-2 bg-bg rounded-md flex-shrink-0">
          <div className="flex items-center gap-1 mb-1">
            <Activity className="h-3 w-3 text-primary" />
            <span className="paragraph-10 font-medium text-dark-grey">Energy Consumption Summary</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="paragraph-9 text-dark-grey mb-1">
                <span style={{ color: '#0E7EE4' }} className="font-medium">CEB-CO</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0">
                <div className="text-[10px] text-dark-grey">
                  Days: <span className="font-medium text-black">{operationStats.cebco.days}</span>
                </div>
                <div className="text-[10px] text-dark-grey">
                  Solar %: <span className="font-medium text-black">{formatNumber(operationStats.cebco.solarPercent, 1)}%</span>
                </div>
                <div className="text-[10px] text-dark-grey">
                  Grid: <span className="font-medium text-black">{formatNumber(operationStats.cebco.totalGrid, 0)} kWh</span>
                </div>
                <div className="text-[10px] text-dark-grey">
                  Solar: <span className="font-medium text-black">{formatNumber(operationStats.cebco.totalSolar, 0)} kWh</span>
                </div>
              </div>
            </div>
            <div>
              <div className="paragraph-9 text-dark-grey mb-1">
                <span className="text-gray-600 font-medium">No CEB-CO</span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0">
                <div className="text-[10px] text-dark-grey">
                  Days: <span className="font-medium text-black">{operationStats.noCebco.days}</span>
                </div>
                <div className="text-[10px] text-dark-grey">
                  Solar %: <span className="font-medium text-black">{formatNumber(operationStats.noCebco.solarPercent, 1)}%</span>
                </div>
                <div className="text-[10px] text-dark-grey">
                  Grid: <span className="font-medium text-black">{formatNumber(operationStats.noCebco.totalGrid, 0)} kWh</span>
                </div>
                <div className="text-[10px] text-dark-grey">
                  Solar: <span className="font-medium text-black">{formatNumber(operationStats.noCebco.totalSolar, 0)} kWh</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div 
        ref={chartRef} 
        className={showOperationView ? '' : 'flex-1'}
        style={{ 
          height: showOperationView ? '300px' : undefined,
          minHeight: showOperationView ? undefined : '250px'
        }} 
      />
    </div>
  );
};